<template>
  <BlockFieldset class="project-rough-planning">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <b-row>
        <b-col lg="5" md="5" sm="12">
          <b-form-group
            horizontal
            :label="$t('operationRegistrationAt')"
            label-for="operationRegistrationAt"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            :invalid-feedback="$t('preconditionIN')"
            :state="isOperationRegistrationRequirementFulfilled('operationRegistrationAt')"
          >
            <Calendar
              v-model="operationRegistrationAt"
              :placeholder="$t('date')"
              :showWeek="true"
              :disabled="isFieldReadOnly"
            >
            </Calendar>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('electricMeterChangedAt')"
            label-for="electricMeterChangedAt"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Calendar
              v-model="electricMeterChangedAt"
              selectionMode="single"
              class="p-column-filter"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date')"
              :showWeek="true"
              :yearNavigator="true"
              :monthNavigator="true"
              :yearRange="yearRange"
              :disabled="isFieldReadOnly"
            >
            </Calendar>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('electrician')"
            label-for="electrician"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            :invalid-feedback="$t('preconditionNA')"
            :state="isNetRegistrationRequirementFulfilled('electrician')"
          >
            <multiselect
              :placeholder="$t('electrician')"
              v-model="project.electrician"
              id="electrician"
              :options="electricianOptions"
              :allow-empty="true"
              :show-labels="false"
              track-by="number"
              label="name"
              :disabled="isFieldReadOnly"
            >
            </multiselect>
          </b-form-group>
        </b-col>
      </b-row>

      <div v-if="!isFieldReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('roughPlanning') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { isRequirementFulfilled } from '@/helpers/registrationRequirements';
import { toProjectRoughPlanningInput } from './projectRoughPlanningInput';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** First year of the year navigator, as in the previous view; the last one is the next year. */
const FIRST_SELECTABLE_YEAR = 2019;

/**
 * Block project-rough-planning ("Grobplanung", see feature page projekt-montage): date of
 * operation registration, date of the meter change and the electrician, chosen from the
 * electricians of the project's organization (`electricianOptions`). The markers NA and IN come
 * from the block's requirement lists. In Legacy-Authz guests see the fields read-only as before;
 * in Matrix-Authz the level decides. On the unsaved project it shows only a hint. Emits `dirty`
 * and `saved`.
 */
export default {
  name: 'ProjectRoughPlanning',
  components: { BlockFieldset, Multiselect, Calendar },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled', 'isGuest']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    /** Legacy-Authz rule: guests may not change the fields; in Matrix-Authz the level decides. */
    isReadOnlyForGuest() {
      return !this.isOrganizationRoleNavigationEnabled && this.isGuest;
    },
    isFieldReadOnly() {
      return this.isBlockReadOnly || this.isReadOnlyForGuest;
    },
    blockInput() {
      return toProjectRoughPlanningInput(this.project);
    },
    /**
     * Electricians of the project's organization. A stored electrician of another organization is
     * added, so it is shown as an option and can be deselected (the server keeps it unchanged and
     * allows clearing it, see feature page elektrofachkraefte).
     */
    electricianOptions() {
      const options = this.project.electricianOptions ?? [];
      const storedElectrician = this.project.electrician;
      const isStoredElectricianMissing =
        storedElectrician && !options.some((option) => option.number === storedElectrician.number);
      return isStoredElectricianMissing ? [...options, storedElectrician] : options;
    },
    yearRange() {
      return `${FIRST_SELECTABLE_YEAR}:${new Date().getFullYear() + 1}`;
    },
    operationRegistrationAt: {
      get() {
        return this.$calendarFormat(this.project.installation?.operationRegistrationAt);
      },
      set(value) {
        this.setInstallationField('operationRegistrationAt', value);
      },
    },
    electricMeterChangedAt: {
      get() {
        return this.$calendarFormat(this.project.installation?.electricMeterChangedAt);
      },
      set(value) {
        this.setInstallationField('electricMeterChangedAt', value);
      },
    },
  },
  methods: {
    ...mapActions(['updateProjectRoughPlanning']),
    saveBlockInput(input) {
      return this.updateProjectRoughPlanning({ projectNumber: this.project.number, input });
    },
    setInstallationField(field, value) {
      if (!this.project.installation) {
        this.$set(this.project, 'installation', {});
      }
      this.$set(this.project.installation, field, value);
    },
    isNetRegistrationRequirementFulfilled(name) {
      return isRequirementFulfilled(this.project.netRegistrationRequirements, name);
    },
    isOperationRegistrationRequirementFulfilled(name) {
      return isRequirementFulfilled(this.project.operationRegistrationRequirements, name);
    },
  },
};
</script>
